import React, {
  memo,
  ReactElement,
  FunctionComponent,
  cloneElement,
  ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Tooltip as ReactTooltip, ITooltip } from 'react-tooltip'

import { useTheme } from '@emotion/react'

import { useUniqId } from '@surglogs-app/hooks'

interface ToolTipProps extends ITooltip {
  maxWidth?: number
}

export const ToolTip = memo(
  ({
    variant = 'light',
    place = 'bottom',
    opacity = 1,
    maxWidth,
    ...rest
  }: ToolTipProps) => {
    const { ui } = useTheme()
    return (
      <ReactTooltip
        {...{
          variant,
          place,
          opacity,
          // needs to be here, because styled does not override styles
          style: {
            fontSize: ui.typography.fontSizes.xxs,
            lineHeight: ui.typography.lineHeights.xxs,
            fontFamily: ui.typography.fontFamilies.default,
            fontWeight: ui.typography.fontWeights.regular,
            color: ui.colors['navy.100'],
            borderRadius: ui.spacings.xs,
            maxWidth: `${maxWidth}px`,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            // need to set this specifically, since react-tooltip does not do it anymore
            zIndex: 999,
            boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
          },
          positionStrategy: 'fixed',
          ...rest,
        }}
      />
    )
  },
)

interface ToolTipContainerProps extends Omit<ToolTipProps, 'id'> {
  tooltipText?: ReactNode
}

export const WrapInToolTip: FunctionComponent<ToolTipContainerProps> = ({
  children,
  tooltipText,
  maxWidth = 400,
  ...rest
}) => {
  const id = useUniqId({ prefix: 'tooltip' })

  return (
    <>
      {cloneElement(children as ReactElement<{ 'data-tooltip-id': string }>, {
        'data-tooltip-id': id,
      })}
      {tooltipText
        ? createPortal(
            <ToolTip {...{ id, maxWidth, ...rest }}>{tooltipText}</ToolTip>,
            document.body,
          )
        : null}
    </>
  )
}
